import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import { MinusIcon, PlusIcon } from "./icons";

const INPUT_BUTTON_CLASSNAME =
  "flex items-center border border-white/20 size-7.5 shrink-0 text-16 justify-center rounded-full";

interface NumberInputProps {
  className?: string;
  inputProps?: React.ComponentProps<"input">;
}

export default function NumberInput({
  className,
  inputProps = {},
}: NumberInputProps) {
  const [value, setValue] = useState(0);

  const handleButton = (mod: number) => () => {
    const newValue = value + mod;

    if (
      newValue >= Number(inputProps.min ?? Number.NEGATIVE_INFINITY) &&
      newValue <= Number(inputProps.max ?? Number.POSITIVE_INFINITY)
    ) {
      setValue(newValue);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(event.target.value);
    setValue(newValue);
    inputProps.onChange?.(event);
  };

  useEffect(() => {
    setValue(Number(inputProps.defaultValue ?? inputProps.value ?? 0));
  }, [inputProps.defaultValue, inputProps.value]);

  const { className: inputClassName, ...restInputProps } = inputProps;

  return (
    <div className={cn("flex items-center gap-2.5", className)}>
      <Button
        aria-label="Decrement"
        className={INPUT_BUTTON_CLASSNAME}
        onClick={handleButton(-1)}
        type="button"
      >
        <MinusIcon />
      </Button>
      <Input
        aria-autocomplete="none"
        autoComplete="off"
        autoCorrect="off"
        className={cn(
          "h-7.5 w-[35px] rounded-[3px] border-white/20 p-0 text-center text-16 text-white placeholder:text-white",
          inputClassName
        )}
        placeholder="Any"
        spellCheck="false"
        value={value || ""}
        {...restInputProps}
        onChange={handleChange}
      />
      <Button
        aria-label="Increment"
        className={INPUT_BUTTON_CLASSNAME}
        onClick={handleButton(1)}
        type="button"
      >
        <PlusIcon />
      </Button>
    </div>
  );
}
